import { _mapValues, _merge } from '@epihub/core/src/fn/object';
import { _uniq } from '@epihub/core/src/fn/array';
import { Color } from '@epihub/web/src/color';
import { Dimension, Screen } from '@epihub/web/src/dimensions';
import { type ThemeDefV0 } from './ThemeDefV0';

export function enhanceThemeDefV0WithTamagui(opts: {
  theme: ThemeDefV0['Theme'];
  creator: ThemeDefV0['Creator'];
  state: ThemeDefV0['State'];
}) {
  opts.theme.tamagui = opts.theme.tamagui ?? opts.creator.tamagui;

  const t = opts.theme.tamagui!.themes[opts.state.scheme === 'light' ? 'light' : 'dark'];

  opts.theme.col = {
    epilogo: new Color('#5240A6'),
    epilogoStrong: new Color('#A600FA'),
    epilogoAccent: new Color('#D15413'),
    light: new Color(t.blue1),
    dark: new Color(t.color12),
    theme: new Color(t.blue9),
    accent: new Color(t.accentBackground),
    primary: new Color(t.color),
    secondary: new Color(t.colorHover),
    tertiary: new Color(t.colorPress),
    error: new Color(t.red9),
    success: new Color(t.green9),
    warning: new Color(t.yellow9),
    info: new Color(t.blue9),

    danger: new Color(t.red9),

    text: new Color(t.color),
    textSecondary: new Color(t.colorHover),
    textTertiary: new Color(t.colorPress),
    textDimmed: new Color(t.colorTransparent),
    textPlaceholder: new Color(t.placeholderColor),
    link: new Color(t.blue9),

    background: new Color(t.background),

    backgroundSecondary: new Color(t.backgroundHover),

    backgroundTertiary: new Color(t.backgroundPress),

    border: new Color(t.borderColor),

    disabled: new Color(t.borderColorHover),

    ..._mapValues(opts.creator.colors, (c) => new Color(c)),
    ...opts.theme.col as Partial<typeof opts.theme.col>,
  };

  const bodyFamily = opts.theme.tamagui!.fonts?.body?.family;
  const headingFamily = opts.theme.tamagui!.fonts?.heading?.family;

  opts.theme.font = _merge({}, opts.theme.font, {
    family: {
      base: [
        ...bodyFamily ? [bodyFamily] : [],
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica',
        'Arial',
        'sans-serif',
        'Apple Color Emoji',
        'Segoe UI Emoji',
      ],
      mono: [
        'SFMono-Regular',
        'Menlo',
        'Monaco',
        'Consolas',
        'Liberation Mono',
        'Courier New',
        'monospace',
      ],
    },
  });

  opts.theme.font.family!.base = _uniq(opts.theme.font.family!.base);
  opts.theme.font.family!.monospace = _uniq(opts.theme.font.family!.monospace);

  opts.theme.font.header = { size: new Dimension('24px') };
  opts.theme.font.base = { size: new Dimension('25px') };

  opts.theme.screen = new Screen(
    typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 375,
    typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 812,
  );
}
